import { Link } from "react-router";
import { Badge, cn, type Tone } from "@/components/ui";
import { formatCompactMoney, formatStage, formatWaiting } from "@/lib/format";
import type { CaseStage, InboxTask, TaskUrgency } from "@/lib/types";

const STAGE_TONE: Record<CaseStage, Tone> = {
  data_collection: "caution",
  underwriting: "accent",
  approval: "positive",
  approved: "positive",
  rejected: "negative",
  closed: "neutral",
};

/**
 * Urgency has to be distinguishable at a glance, so each level gets its own
 * glyph as well as its own colour — colour alone would fail for anyone who
 * cannot separate red from amber, and three identical warning triangles
 * communicate nothing.
 */
const URGENCY: Record<TaskUrgency, { glyph: string; className: string; label: string }> = {
  blocking: { glyph: "⚠", className: "text-negative", label: "Blocked" },
  attention: { glyph: "●", className: "text-caution", label: "Needs attention" },
  routine: { glyph: "✓", className: "text-positive", label: "On track" },
};

/** `/cases/acme-2026-wc/evidence` — the overview tab has no path segment. */
function taskHref(task: InboxTask): string {
  const base = `/cases/${task.caseId}`;
  return task.target === "overview" ? base : `${base}/${task.target}`;
}

export function TaskTable({ tasks }: { tasks: InboxTask[] }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-line text-left">
          <th scope="col" className="eyebrow px-6 py-2 font-semibold">
            Borrower
          </th>
          <th scope="col" className="eyebrow px-3 py-2 font-semibold">
            Task
          </th>
          <th scope="col" className="eyebrow px-3 py-2 text-right font-semibold">
            Amount
          </th>
          <th scope="col" className="eyebrow px-3 py-2 font-semibold">
            Stage
          </th>
          <th scope="col" className="eyebrow px-6 py-2 text-right font-semibold">
            Waiting
          </th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((task) => (
          <tr key={task.id} className="group border-b border-line last:border-0 hover:bg-accent-soft/40">
            <td className="px-6 py-3">
              {/*
                One link spans the row via an overlay, so the whole row is a
                click target while the accessible name stays on the borrower.
              */}
              <Link to={taskHref(task)} className="relative font-medium text-ink hover:text-accent">
                <span className="absolute inset-y-[-0.75rem] left-[-1.5rem] right-[-100vw] z-10" />
                {task.borrowerName}
              </Link>
            </td>
            <td className="px-3 py-3 text-ink-muted">
              <span className="flex items-center gap-1.5">
                <span
                  title={URGENCY[task.urgency].label}
                  className={cn("shrink-0 select-none text-xs", URGENCY[task.urgency].className)}
                >
                  <span className="sr-only">{URGENCY[task.urgency].label}: </span>
                  <span aria-hidden>{URGENCY[task.urgency].glyph}</span>
                </span>
                {task.task}
              </span>
            </td>
            <td className="numeric px-3 py-3 text-right font-medium">
              {formatCompactMoney(task.amount)}
            </td>
            <td className="px-3 py-3">
              <Badge tone={STAGE_TONE[task.stage]}>{formatStage(task.stage)}</Badge>
            </td>
            <td className="numeric px-6 py-3 text-right text-ink-muted">
              {formatWaiting(task.waitingDays)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
